import React, { FC, useCallback, useRef, useState } from 'react';
import { GoogleReCaptcha, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import AlertTemplate, { AlertTypes } from '@components/Alert/Alert';
import useTranslations from '@hooks/useTranslation';
import ButtonTemplate from '@ui/Button/Button';
import CheckboxTemplate from '@ui/Checkbox/Checkbox';
import Title, { TitleTagTypes } from '@ui/SectionTitle/Title';
import { EMAIL_REGEX } from '../../constants';
import { contactApi } from '../../services/contact.service';
import ContentTemplate from '../../ui/Content/Content';
import {
    Agreement,
    AgreementLink,
    Background,
    ErrorMessage,
    Form,
    GlobalErrorMessage,
    Input,
    SuccessMessage,
    TextArea,
    Wrapper,
} from './ContactForm.elements';

export interface ContactFormProps {
    withoutBackground?: boolean;
}

const ContactFormTemplate: FC<ContactFormProps> = ({ withoutBackground }) => {
    const { t } = useTranslations();

    const nameInputRef = useRef<HTMLInputElement>();
    const emailInputRef = useRef<HTMLInputElement>();
    const phoneInputRef = useRef<HTMLInputElement>();
    const messageInputRef = useRef<HTMLTextAreaElement>();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [hasAgreement, setHasAgreement] = useState(false);
    const [hasNameError, setHasNameError] = useState(false);
    const [hasEmailError, setHasEmailError] = useState(false);
    const [hasMessageError, setMessageError] = useState(false);
    const [hasSentError, setHasSentError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSent, setIsSent] = useState(false);

    const handleClick = async () => {
        setHasNameError(false);
        setHasEmailError(false);
        setMessageError(false);
        setHasSentError(false);
        setIsSent(false);

        const hasError = false;

        if (nameInputRef?.current?.value === '') {
            setHasNameError(true);
            return;
        }

        if (emailInputRef?.current?.value === '' || !EMAIL_REGEX.test(emailInputRef?.current?.value)) {
            setHasEmailError(true);
            return;
        }

        if (messageInputRef?.current?.value === '') {
            setMessageError(true);
            return;
        }

        if (hasError) {
            return;
        }

        try {
            setIsLoading(true);
            const generatedToken = await executeRecaptcha();

            const data = {
                ...(nameInputRef?.current?.value && { name: nameInputRef?.current?.value }),
                email: emailInputRef?.current?.value,
                ...(phoneInputRef?.current?.value && { phone: phoneInputRef?.current?.value }),
                message: messageInputRef?.current?.value,
                token: generatedToken,
            };

            await contactApi.contact(data);
            setIsSent(true);
            setIsLoading(false);

            emailInputRef.current.value = '';
            nameInputRef.current.value = '';
            phoneInputRef.current.value = '';
            messageInputRef.current.value = '';
        } catch {
            setHasSentError(true);
        }
    };

    return (
        <Background withoutBackground={withoutBackground}>
            {hasSentError && <AlertTemplate type={AlertTypes.error} message={t('contact.error')} />}
            <ContentTemplate withPadding>
                <Wrapper>
                    <Form>
                        <Title tagType={TitleTagTypes.H3} isBlack>
                            {t('contact.contact.title')}
                        </Title>
                        <Input
                            ref={nameInputRef}
                            placeholder={t('contact.form.name.placeholder')}
                            className={`${hasNameError ? 'error' : ''}`}
                        />
                        {hasNameError && <ErrorMessage>{t('contact.form.name.error')}</ErrorMessage>}
                        <Input
                            ref={emailInputRef}
                            placeholder={t('contact.form.email.placeholder')}
                            className={`${hasEmailError ? 'error' : ''}`}
                        />
                        {hasEmailError && <ErrorMessage>{t('contact.form.email.error')}</ErrorMessage>}
                        <Input ref={phoneInputRef} placeholder={t('contact.form.phone.placeholder')} />
                        <TextArea
                            ref={messageInputRef}
                            placeholder={t('contact.form.message.placeholder')}
                            className={`${hasMessageError ? 'error' : ''}`}
                        />
                        {hasMessageError && <ErrorMessage>{t('contact.form.message.error')}</ErrorMessage>}
                        <Agreement>
                            <CheckboxTemplate
                                name="agreement"
                                onChange={() => setHasAgreement(!hasAgreement)}
                                checked={hasAgreement}
                            >
                                Súhlasím s{' '}
                                <AgreementLink href="https://www.zakon.sk/ochrana-osobnych-udajov.xhtml">
                                    podmienkami spracovania osobných údajov
                                </AgreementLink>
                            </CheckboxTemplate>
                        </Agreement>
                        {(hasNameError || hasEmailError || hasMessageError) && (
                            <GlobalErrorMessage>{t('contact.form.error')}</GlobalErrorMessage>
                        )}
                        {!isSent && (
                            <ButtonTemplate
                                isLoading={isLoading}
                                disabled={!hasAgreement}
                                marginTop={25}
                                onClick={handleClick}
                                text="Odoslať"
                            />
                        )}
                        {isSent && <SuccessMessage>{t('contact.form.sent')}</SuccessMessage>}
                    </Form>
                </Wrapper>
            </ContentTemplate>
        </Background>
    );
};

export default ContactFormTemplate;
